import { children, slugs } from '../../contact.meta';

/**
 * Both the form's `variant` and the child route (`/new/contact/$audience`), so the audience survives a
 * link and decides the lead's backend `category` (TEC-3821 B13). The param is this internal name; the
 * router's rewrite localizes the segment (`contact.meta.ts`).
 */
export const AUDIENCES = ['company', 'ngo', 'donor', 'press'] as const;

export type Audience = (typeof AUDIENCES)[number];

const isAudience = (value: string): value is Audience => (AUDIENCES as readonly string[]).includes(value);

/** Null is the 404. */
export function parseAudience(param: string | undefined): Audience | null {
	return param !== undefined && isAudience(param) ? param : null;
}

/**
 * Only `enquire` gets a URL segment in `contact.meta.ts`, so a form can be linked; the default
 * `collaborate` owns the bare `/contacto/ong`, or one page would have two addresses.
 */
export const NGO_TABS = ['collaborate', 'enquire'] as const;

export type NgoTab = (typeof NGO_TABS)[number];

export const DEFAULT_NGO_TAB: NgoTab = 'collaborate';

/** Null is the 404. Read off the meta's tree, so a tab with a route but no declared segment cannot exist. */
export function parseTab(audience: Audience | null, param: string | undefined): NgoTab | null {
	if (!audience || param === undefined) return null;
	const declared = children[audience]?.children;
	if (!declared || !Object.hasOwn(declared, param)) return null;
	return NGO_TABS.find((tab) => tab === param) ?? null;
}

/** Joined with `/` (`{ es: 'contacto/ong/consulta' }`), all `PageSeo`'s canonical and hreflang need. */
export function audiencePageSlugs(audience: Audience, tab?: NgoTab): Record<string, string> {
	const audienceNode = children[audience];
	const tabNode = tab ? audienceNode?.children?.[tab] : undefined;
	return Object.fromEntries(
		Object.entries(slugs).map(([locale, slug]) => {
			const below = [audienceNode?.slugs[locale] ?? audience, ...(tab ? [tabNode?.slugs[locale] ?? tab] : [])];
			return [locale, [slug, ...below].join('/')];
		}),
	);
}
